/**
 * DRPX Courier — Realtime-Hook für Aufträge + Angebote
 *
 * Security: Aufträge/Angebote-Channels haben keinen serverseitigen Filter
 * (Supabase Realtime unterstützt nur einfache eq-Filter, keine RLS).
 * Deshalb filtern wir client-seitig: nur Events verarbeiten, die den
 * aktuellen User betreffen.
 *
 * @module hooks/useRealtime
 */

import { useEffect } from 'react';
import { db } from '../lib/supabase.js';
import type { Profile, Auftrag, Angebot, Rolle } from '../types.js';
import { istFirmenZugang } from '../lib/zugang.js';

type SetAuftraege = React.Dispatch<React.SetStateAction<Auftrag[]>>;

interface UseRealtimeOptions {
  user: Profile | null;
  setAuftraege: SetAuftraege;
}

/**
 * Realtime-Hook: Supabase Realtime Subscriptions für Aufträge + Angebote.
 * Filtert Events client-seitig nach Relevanz (eigene Aufträge, Marktplatz).
 */
export function useRealtime({ user, setAuftraege }: UseRealtimeOptions): void {
  useEffect(
    function () {
      if (!user) return;
      const userId = user.id;
      const rolle = user.rolle;

      // ── Aufträge ──
      const chAuf = db.subscribeAuftraege(
        // INSERT
        function onInsert(a: Auftrag) {
          if (!isAuftragRelevant(a, userId, rolle)) return;
          setAuftraege(function (prev) {
            if (prev.some(function (x) { return x.id === a.id; })) return prev;
            return [a].concat(prev);
          });
        },
        // UPDATE
        function onUpdate(a: Auftrag) {
          setAuftraege(function (prev) {
            return prev.map(function (x) {
              return x.id === a.id
                ? Object.assign({}, x, a, { angebote: x.angebote || [] })
                : x;
            });
          });
        },
        // DELETE
        function onDelete(id: string) {
          if (id) {
            setAuftraege(function (prev) {
              return prev.filter(function (x) { return x.id !== id; });
            });
          }
        },
      );

      // ── Angebote ──
      const chAng = db.subscribeAngebote(
        // INSERT
        function onInsert(ang: Angebot) {
          setAuftraege(function (prev) {
            return prev.map(function (a) {
              return a.id === ang.auftragId
                ? Object.assign({}, a, {
                    angebote: (a.angebote || []).some(function (x) { return x.id === ang.id; })
                      ? a.angebote
                      : (a.angebote || []).concat([ang]),
                    status: a.status === 'offen' ? 'angebote' : a.status,
                  })
                : a;
            });
          });
        },
        // UPDATE
        function onUpdate(ang: Angebot) {
          setAuftraege(function (prev) {
            return prev.map(function (a) {
              return a.id === ang.auftragId
                ? Object.assign({}, a, {
                    angebote: (a.angebote || []).map(function (x) {
                      return x.id === ang.id ? ang : x;
                    }),
                  })
                : a;
            });
          });
        },
        // DELETE
        function onDelete(angId: string, auftragId?: string) {
          setAuftraege(function (prev) {
            return prev.map(function (a) {
              if (a.id !== auftragId) return a;
              const newAng = (a.angebote || []).filter(function (x) {
                return x.id !== angId;
              });
              // Audit-074 (15.05.2026, Befund #2):
              // Bei Annahme eines Angebots werden in der RPC alle anderen
              // Angebote zum gleichen Auftrag gelöscht (DELETE-Events) UND
              // der Auftrag-Status auf 'vergeben' gesetzt (UPDATE-Event).
              // Diese Events kommen unabhängig voneinander via Realtime —
              // die Reihenfolge ist nicht garantiert. Wenn die DELETEs
              // zuerst eintreffen, hätte die alte Bedingung
              // `newAng.length === 0 && a.status === 'angebote'`
              // den Status fälschlich auf 'offen' zurückgesetzt, weil das
              // UPDATE-Event mit status='vergeben' noch nicht da war.
              // Zusatz-Guard `!a.vergebenAn`: wenn der Auftrag bereits
              // einen vergebenAn-Wert hat (= gerade vergeben), kein
              // Reset auf 'offen' erlauben. Das UPDATE-Event korrigiert
              // dann den Status sauber auf 'vergeben'.
              const sollResetAufOffen =
                newAng.length === 0
                && a.status === 'angebote'
                && !a.vergebenAn;
              return Object.assign({}, a, {
                angebote: newAng,
                status: sollResetAufOffen ? 'offen' : a.status,
              });
            });
          });
        },
      );

      return function () {
        db.unsubscribe(chAuf);
        db.unsubscribe(chAng);
      };
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [user && user.id],
  );
}

/**
 * Security-Filter: Prüft ob ein Auftrag für den aktuellen User relevant ist.
 * - Admin: alles sehen
 * - Firma und ihre Mitarbeiter: nur eigene Aufträge (`firmaId === userId`)
 * - Fahrer: offene Marktplatz-Aufträge + Aufträge mit eigenem Angebot
 */
export function isAuftragRelevant(
  auftrag: Auftrag,
  userId: string,
  rolle: Rolle,
): boolean {
  if (rolle === 'admin') return true;
  // Mitarbeiter (DRPX-Pro): userId ist die Firma (Weg B) — wie der Chef.
  if (istFirmenZugang({ rolle })) return auftrag.firmaId === userId;
  // Fahrer: offene/angebote (Marktplatz) oder eigenes Angebot
  if (['offen', 'angebote'].includes(auftrag.status)) return true;
  if (
    (auftrag.angebote || []).some(function (ang) {
      return ang.fahrerId === userId;
    })
  ) return true;
  return false;
}
